import React from "react";
import { Link } from "react-router-dom";
import { Brain, FileSearch, Zap, RotateCcw } from "lucide-react";
import { LogoCandidateDark } from "./Logo";

const FEATURES = [
  { icon: Brain,      title: "AI Mock Interviews", body: "Practice real questions from curated roles." },
  { icon: FileSearch, title: "Resume Analyzer",    body: "Instant ATS score, gap analysis, and fixes." },
  { icon: Zap,        title: "Autopilot Apply",    body: "Matched roles, applied for you." },
  { icon: RotateCcw,  title: "Question Retakes",   body: "Retry answers and track improvement." },
];

const STATS = [
  ["4",      "AI prep modules"],
  ["0–100",  "ATS match score"],
  ["Daily",  "Job matches"],
  ["Private", "On-device tracking"],
];

/**
 * Shared right-hand brand panel for Login + Signup.
 * Navy background, gradient orbs, feature highlights, and honest product stats.
 */
export default function AuthBrandPanel({
  eyebrow = "WHAT YOU UNLOCK",
  title = "Everything you need to ace your next interview.",
  subtitle = "From AI practice to smart job matching — built for candidates who want results.",
}) {
  return (
    <div className="hidden lg:flex flex-col bg-[#0F172A] text-white p-12 relative overflow-hidden">

      {/* Glowing orb — top-right */}
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(99,102,241,0.18) 0%, rgba(37,99,235,0.12) 40%, transparent 70%)" }} />
      {/* Secondary orb — bottom-left */}
      <div className="absolute -bottom-24 -left-24 w-[320px] h-[320px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(37,99,235,0.10) 0%, transparent 65%)" }} />

      <div className="relative z-10 flex flex-col h-full">
        {/* Wordmark */}
        <Link to="/" className="w-fit opacity-90 hover:opacity-100 transition-opacity">
          <LogoCandidateDark className="h-12 w-auto" />
        </Link>

        <div className="mt-12 mb-10">
          <div className="font-mono text-[10px] text-[#818CF8] tracking-[0.25em] mb-3">{eyebrow}</div>
          <h2 className="font-display font-black text-4xl leading-[1.15] tracking-tight">{title}</h2>
          <p className="text-white/45 mt-4 text-sm leading-relaxed max-w-sm">{subtitle}</p>
        </div>

        <div className="space-y-5 flex-1">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex items-start gap-4 group">
              <div className="w-9 h-9 flex-shrink-0 rounded-lg border border-white/[0.08] flex items-center justify-center bg-white/[0.04] group-hover:border-indigo-400/40 group-hover:bg-indigo-500/10 transition-all duration-300">
                <Icon className="w-4 h-4 text-white/50 group-hover:text-indigo-300 transition-colors duration-300" />
              </div>
              <div>
                <div className="font-semibold text-sm text-white/85 mb-0.5">{title}</div>
                <div className="text-xs text-white/35 leading-relaxed">{body}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-8 border-t border-white/[0.08] grid grid-cols-2 gap-5">
          {STATS.map(([val, lbl]) => (
            <div key={lbl}>
              <div className="font-display font-black text-2xl text-white">{val}</div>
              <div className="font-mono text-[10px] text-white/30 tracking-widest mt-0.5 uppercase">{lbl}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
